<template>
    <Modal class="metric-set-modal" v-on="listeners" v-bind="$props" showHeaderSeparator>
        <div slot="modal-body" class="custom-modal-body">
            <div class="select-metrics-text">Select the desired metrics for each tab:</div>
            <div v-for="(metricSet, metricSetIndex) in $v.sortedMetricSets.$each.$iter"
                 :key="`${metricSet.id}${metricSetIndex}`"
                 class="metric-set-container">
                <div v-if="metricSet.$model.isDefault" class="metric-set-row-default">
                    <strong>{{metricSet.$model.name}}:</strong>
                </div>
                <div v-if="!metricSet.$model.isDefault" class="metric-set-row">
                    <label>
                        <input type="text" v-model="metricSet.name.$model" placeholder="Enter metric set name"
                            id="metricSetName">
                        <div class="error-message font-7"
                             v-if="$v.$anyError && !metricSet.name.required">Metric set name is a mandatory field</div>
                    </label>
                    <button type="button"
                            class="button-linkStyle font-5 delete-set-button"
                            @click="removeMetricSet(metricSet.$model)">
                        <IconBase iconName="deleteIcon" width="12" height="15" class="delete-icon"></IconBase>
                        <span>Delete Set</span>
                    </button>
                </div>
                <div class="metric-set-dropdowns-container">
                    <div v-for="dropDownIndex in metricSetKpisCount"
                         :key="`${metricSet.id}${metricSetIndex}_dropdown${dropDownIndex}`"
                         class="metric-set-dropdown-container">
                        <div class="metric-set-dropdown">
                            <span class="font-3 metric-set-dropdown-index">{{dropDownIndex}}</span>
                            <Dropdown :uniqueId="`${metricSet.id}${metricSetIndex}_dropdown${dropDownIndex}`"
                                      :options="kpiOptions"
                                      :selectedId="metricSet.$model.metricsData[dropDownIndex - 1]"
                                      height="34"
                                      selectedDisplayTextField="titleText"
                                      :searchEnabled="false"
                                      defaultText="Select Metric"
                                      class="metrics-data-dropdown"
                                      markSelected
                                      @dropdownSelectedItemChanged="metricSetDataChanged({
                                        metricSet: metricSet.$model,
                                        metricSetDataIndex: dropDownIndex - 1,
                                        metricSetData: $event
                                      })"></Dropdown>
                        </div>

                        <div class="error-message font-7"
                             v-if="dropDownIndex === 1 && !metricSet.metricsData.required && $v.$anyError">
                            Metric set can't be empty
                        </div>
                    </div>
                </div>
                <div class="error-message font-7"
                     v-if="!metricSet.metricsData.noDuplications && $v.$anyError">
                    Metric set can't contain the same metric twice
                </div>
            </div>
            <button @click="addMetricSet" class="new-metric-set-button" id="newMetricSetButton"
                    :disabled="sortedMetricSets.length >= 5">+ New Metric Set</button>
        </div>
    </Modal>
</template>

<script>
import Modal from 'components/modals/Modal.vue';
import IconBase from 'components/IconBase.vue';
import Dropdown from 'components/dropdowns/Dropdown.vue';
import kpisTextMapping from 'monetize/reports/utils/kpisTextMapping';
import validators from 'services/libWrappers/vuelidate/validators';

const { required, minLength } = validators;
const metricSetKpisCount = 5;

export default {
    name: 'CustomizeMetricSetModal',
    components: { Dropdown, IconBase, Modal },
    props: {
        ...Modal.props,
        metricSets: Array,
        availableKpis: Array
    },
    data() {
        return {
            sortedMetricSets: this.metricSets.map((metricSet) => ({
                ...metricSet,
                metricsData: [...metricSet.metricsData]
            }))
        };
    },
    computed: {
        listeners() {
            // we bind the @ok listener to override it and emit an ok event with arguments (the component data)
            return { ...this.$listeners, ok: this.emitOk };
        },
        kpiOptions() {
            return this.availableKpis.map((availableKpi) => ({
                id: availableKpi,
                titleText: kpisTextMapping[availableKpi].name,
                displayText: kpisTextMapping[availableKpi].name,
                description: kpisTextMapping[availableKpi].tooltip
            }));
        },
        metricSetKpisCount() {
            return metricSetKpisCount;
        }
    },
    methods: {
        emitOk() {
            this.$v.sortedMetricSets.$touch();
            if (this.$v.$anyError) {
                return;
            }

            this.$emit('ok', {
                metricSets: this.sortedMetricSets.map((sortedMetricSet) => ({
                    ...sortedMetricSet,
                    metricsData: sortedMetricSet.metricsData.filter(Boolean)
                }))
            });
        },
        addMetricSet() {
            this.sortedMetricSets = [...this.sortedMetricSets, {
                id: 'new',
                name: '',
                metricsData: [],
                isDefault: false
            }];
        },
        removeMetricSet(metricSet) {
            this.sortedMetricSets = this.sortedMetricSets.filter((ms) => ms !== metricSet);
        },
        metricSetDataChanged({
            metricSet,
            metricSetDataIndex,
            metricSetData: { id: metricSetDataId }
        }) {
            this.$set(metricSet.metricsData, metricSetDataIndex, metricSetDataId);
        }
    },
    validations: {
        sortedMetricSets: {
            required,
            $each: {
                name: {
                    required
                },
                metricsData: {
                    required,
                    minLength: minLength(1),
                    noDuplications: (value) => {
                        const countMetricsMap = value.reduce((acc, b) => ({
                            ...acc,
                            [b]: (acc[b] || 0) + 1
                        }), {});
                        const duplicates = Object.keys(countMetricsMap).filter((metric) => countMetricsMap[metric] > 1);
                        return duplicates.length === 0;
                    }
                }
            }
        }
    }
};
</script>

<style lang="scss" scoped>
@import "~styles/styleGuide.V2";
@import "~styles/buttons.scss";
@import "~styles/input.scss";

.metric-set-modal.v1 ::v-deep .modal-container,
.metric-set-modal.v2 ::v-deep .modal-container {
    width: 885px;
}

.metric-set-modal ::v-deep .modal-body {
    margin: 20px 24px 20px 24px;
    padding: 0;
}

.metric-set-modal ::v-deep .modal-header {
    padding: 18px 30px 0 20px;
}

.metric-set-container {
    border: $border;
    border-radius: $borderRadius;
    margin: 10px 0 10px;
    padding: 15px;
}

.metric-set-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 11px;
}

.metric-set-row-default {
    margin-bottom: 12px;
    line-height: 12px;
}

.delete-icon {
    vertical-align: top;
    margin-right: 4px;
}

.delete-set-button {
    align-self: flex-start;
    height: auto;
    margin-right: 5px;
}

.metric-set-dropdowns-container {
    display: flex;
}

.metric-set-dropdown-container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 0 8px;

    &:first-child {
        padding: 0 8px 0 0;
    }

    &:last-child {
        padding: 0 0 0 8px;
    }
}

.metric-set-dropdown {
    display: flex;
    flex: 1;
}

.metric-set-dropdown-index {
    border-bottom-left-radius: $borderRadius;
    border-top-left-radius: $borderRadius;
    background-color: $light-200;
    border: $border;
    display: inline-flex;
    height: 32px;
    line-height: 32px;
    width: 32px;
    min-width: 32px;
    justify-content: center;
}

.error-message {
    color: $negative;
    line-height: 20px;
}

.metrics-data-dropdown {
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
}

.new-metric-set-button {
    margin-top: 5px;
    margin-left: 4px;
}

.select-metrics-text {
    margin-bottom: 14px;
}
</style>
